import type { Channel } from '@/features/transactions/types'

/**
 * AF-126 — Alerts queue + detail data shapes.
 *
 * The wire format follows the backend AF-111 alerts API (alert summary in the
 * list endpoint, full detail with `notes`, `status_history`, and embedded
 * transaction in the detail endpoint). Mutation payloads are typed
 * separately so we can derive optimistic-update shapes cleanly.
 */

export type Priority = 'critical' | 'high' | 'medium' | 'low'

// Mirrors entities/alert.go's AlertStatus, plus the collapsed
// wire-only "closed" value the backend emits (uiAlertStatus in
// internal/api/alerts_handler.go buckets every closed_* + merged
// down to "closed" — callers that need the granular reason read
// `closed_resolution` instead). The granular `closed_*` variants
// are still accepted on the filter side (e.g. ?status=closed_fraud)
// and survive in mock fixtures, so they stay in the union.
export type AlertStatus =
  | 'open'
  | 'in_review'
  | 'escalated'
  | 'merged'
  | 'closed'
  | 'closed_fraud'
  | 'closed_false_positive'
  | 'closed_duplicate'
  | 'closed_other'

export const ALERT_CLOSED_STATUSES: readonly AlertStatus[] = [
  'closed',
  'closed_fraud',
  'closed_false_positive',
  'closed_duplicate',
  'closed_other',
] as const

export function isClosedStatus(s: AlertStatus): boolean {
  return (
    s === 'closed' ||
    s === 'closed_fraud' ||
    s === 'closed_false_positive' ||
    s === 'closed_duplicate' ||
    s === 'closed_other'
  )
}

// "merged" is terminal but not closed — alert lives inside its case
// envelope. Mirrors AlertStatus.IsTerminal in entities/alert.go.
export function isTerminalStatus(s: AlertStatus): boolean {
  return s === 'merged' || isClosedStatus(s)
}

export type Resolution =
  | 'false_positive'
  | 'fraud_confirmed'
  | 'insufficient_evidence'
  | 'duplicate'
  | 'customer_confirmed_legitimate'
  | 'other'

export interface Assignee {
  user_id: string
  display_name: string
  /** Initials precomputed by backend so the avatar never has to derive them. */
  initials: string
}

export interface CustomerSnapshot {
  id: string
  customer_number: string
  display_name: string
  /** 0–100. */
  risk_score: number | null
  /** Last 30d snapshot for the sparkline. */
  risk_score_history: number[]
  kyc_tier: 'tier_1' | 'tier_2' | 'tier_3' | 'unverified'
  pep: boolean
  bvn_masked: string | null
}

export interface SlaInfo {
  /** ISO timestamp the SLA started counting from. */
  started_at: string
  /** ISO timestamp the SLA expires (or expired). */
  deadline_at: string
  /** Configured target in minutes — used to render progress percentage. */
  target_minutes: number
}

export interface AlertSummary {
  id: string
  alert_number: string
  scenario_code: string
  scenario_name: string
  priority: Priority
  /** 0–100. */
  score: number
  status: AlertStatus
  channel: Channel
  /** Stored as kobo. Null when alert isn't tied to a single transaction. */
  amount_minor: number | null
  customer: { id: string; display_name: string; customer_number: string }
  queue_id: string
  queue_name: string
  assigned_to: Assignee | null
  sla: SlaInfo
  created_at: string
  updated_at: string
  /** True when sla.deadline_at is in the past and alert is still open. */
  sla_breached: boolean
  /** Set when this alert has been grouped into a case (AF-127). */
  case_id: string | null
  case_number: string | null
  /** Branch chip — derived from the triggering transaction's branch. */
  branch_code?: string
  branch_name?: string
}

export interface AlertNote {
  id: string
  body: string
  author: Assignee
  created_at: string
}

export interface StatusHistoryEntry {
  id: string
  from_status: AlertStatus | null
  to_status: AlertStatus
  by: Assignee | null
  at: string
  reason: string | null
}

export interface RuleHit {
  rule_code: string
  rule_name: string
  /** Boolean signals fired; multiplier-style numeric signals included where present. */
  evidence: Record<string, string | number | boolean>
}

export interface AlertDetail extends AlertSummary {
  /** Reason codes from the scenario engine — short tokens like 'NAME_MISMATCH'. */
  reason_codes: string[]
  rules_fired: RuleHit[]
  /** AF-125 transaction ID; null when alert is customer-level (e.g. profile-change scenarios). */
  transaction_id: string | null
  customer_snapshot: CustomerSnapshot
  related_alert_ids: string[]
  notes: AlertNote[]
  status_history: StatusHistoryEntry[]
  resolution: Resolution | null
  /**
   * Present when this alert was minted by the screening pipeline
   * (one or more screening.matches reference this alert). Drives the
   * "View screening" header chip on the alert detail page so analysts
   * can pivot back to /screening/screenings/{public_id}.
   */
  screening?: AlertScreeningLink | null
  /**
   * Present when this alert was minted by the adverse-media pipeline
   * (an adverse_media.classifications row references this alert via
   * alert_id). Drives the "View adverse media" header chip on the
   * alert detail page so analysts can pivot to
   * /adverse-media/classifications/{public_id}.
   */
  adverse_media?: AlertAdverseMediaLink | null
}

/** Screening provenance for an alert that came from the screening pipeline. */
export interface AlertScreeningLink {
  public_id: string
  list_code: string
  score_band: 'weak' | 'review' | 'block'
  composite_score: number
  entity_name?: string
}

/** Adverse-media provenance for an alert that came from the adverse-media pipeline. */
export interface AlertAdverseMediaLink {
  /** classification public_id — for deep link to /adverse-media/classifications/{id} */
  public_id: string
  /** Risk category from the classification (e.g. 'fraud', 'corruption'). */
  category?: string
  /** none | low | medium | high | critical */
  severity: 'none' | 'low' | 'medium' | 'high' | 'critical'
  /** 0..1 confidence the LLM emitted. */
  confidence: number
  /** Source feed tier (T1..T4) at classification time. */
  source_credibility_tier?: 'T1' | 'T2' | 'T3' | 'T4'
  /** Optional article title for the chip tooltip. */
  article_title?: string
  /** Optional subject name for human context (e.g. 'Aisha Yusuf'). */
  subject_name?: string
}

// ---------- query shape ----------

export type SortDir = 'asc' | 'desc'
export interface SortKey {
  field: keyof AlertSummary | 'score' | 'amount_minor' | 'sla_remaining'
  dir: SortDir
}

export interface AlertFilters {
  status?: AlertStatus[]
  priority?: Priority[]
  scenario?: string[]
  queue_id?: string[]
  channel?: Channel[]
  assigned_to?: string[] | 'unassigned'
  sla_at_risk?: boolean
  sla_breached?: boolean
  amount_min_minor?: number
  amount_max_minor?: number
  date_from?: string
  date_to?: string
  /** Free-text search across alert number + scenario name + customer. */
  q?: string
}

export interface SavedFilter {
  id: string
  name: string
  filters: AlertFilters
  /** Server-computed live count, refreshed via WebSocket. */
  count: number
  /** Built-in sets are not user-deletable. */
  builtin: boolean
}

export interface AlertsListResponse {
  alerts: AlertSummary[]
  total: number
  page: number
  page_size: number
  /** Live counts for the sidebar — keyed by queue_id. */
  queue_depths: Record<string, number>
}

// ---------- mutations ----------

export interface BulkAssignPayload {
  alert_ids: string[]
  user_id: string
}

export interface BulkClosePayload {
  alert_ids: string[]
  resolution: Resolution
  reason?: string
}

export interface BulkEscalatePayload {
  alert_ids: string[]
  reason: string
}

export interface AddNotePayload {
  alert_id: string
  body: string
}

// ---------- WebSocket events ----------

export type AlertWsEvent =
  | { type: 'alerts.created'; payload: AlertSummary }
  | { type: 'alerts.updated'; payload: AlertSummary }
  | { type: 'alerts.assigned'; payload: { id: string; assigned_to: Assignee | null } }
  | { type: 'alerts.status_changed'; payload: { id: string; status: AlertStatus } }
  | { type: 'alerts.deleted'; payload: { id: string } }

// ---------- additional payloads + entities ----------

export interface BulkLinkToCasePayload {
  alert_ids: string[]
  case_id: string | null
  /** When `case_id` is null, server creates a new case with this title. */
  new_case_title?: string
  /**
   * Optional richer case-creation fields. The wizard surfaces them
   * prefilled from the selected alerts so the analyst doesn't have
   * to re-type what's already on screen. Server uses them when
   * `case_id` is null; ignored on the link-existing branch.
   */
  new_case_summary?: string
  new_case_priority?: 'low' | 'medium' | 'high' | 'critical'
  new_case_queue?: string
}

/**
 * CaseSummary mirrors the slimmed-down list-row shape every cases
 * surface uses. The backend wire response carries more fields (see
 * CaseRichSummary below) — the AlertLinker picker only reads
 * id/title/status/alert_count, so this stays small + the richer
 * page reads through CaseRichSummary instead.
 */
/**
 * Backend wire statuses are `open | in_progress | pending_info |
 * escalated | resolved | closed` (see entities/case.go). The legacy
 * UX-friendly aliases (`in_review`, `fraud_confirmed`,
 * `false_positive`) live alongside them so the cases list / KPI
 * tiles that already key on those values keep working — the panel
 * dispatch + the workflow API consumers must use the wire values
 * instead.
 */
export type CaseWireStatus =
  | 'open'
  | 'in_progress'
  | 'pending_info'
  | 'escalated'
  | 'resolved'
  | 'closed'

export type CaseUiStatus = 'in_review' | 'fraud_confirmed' | 'false_positive'

export interface CaseSummary {
  id: string
  case_number: string
  title: string
  status: CaseWireStatus | CaseUiStatus
  alert_count: number
}

/**
 * CaseRichSummary is the full list-row shape the cases-list page
 * consumes. The backend's GET /cases endpoint returns this — fields
 * are nullable because legacy rows may pre-date the column and the
 * search-result variant carries a `relevance` score the standard
 * list omits.
 */
export interface CaseRichSummary extends CaseSummary {
  priority: 'critical' | 'high' | 'medium' | 'low'
  queue?: string
  summary?: string
  assigned_to_id?: number | null
  // Hydrated by the server: user_id (public_id) + display_name +
  // initials. Older clients still receive the bare assigned_to_id
  // and rendered "user #<id>" — assigned_to is the new field they
  // should bind to when present.
  assigned_to?: Assignee | null
  risk_score?: number | null
  sla_deadline?: string | null
  sla_breached?: boolean
  sla_breached_at?: string | null
  resolved_at?: string | null
  closed_at?: string | null
  created_at: string
  updated_at: string
  relevance?: number | null
}

export interface AnalystSummary {
  user_id: string
  display_name: string
  initials: string
  /** Live workload for the assignment picker — open + in_review counts. */
  open_count: number
}

export interface ExportJob {
  id: string
  status: 'queued' | 'running' | 'ready' | 'failed'
  /** Set when status === 'ready'. Signed URL valid for 5 minutes. */
  download_url: string | null
  /** Set when status === 'failed'. */
  error: string | null
  /** Number of rows in the export. */
  row_count: number
}

export interface CreateSavedFilterPayload {
  name: string
  filters: AlertFilters
}
